import type { AuditLogDto } from '@bravo/shared';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Empty, ErrorBlock, Field, PageHeader, Spinner, TextInput } from '../components/ui';
import { apiPage } from '../lib/api';
import { useFormat } from '../lib/format';

export function AuditPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const [action, setAction] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const query = useInfiniteQuery({
    queryKey: ['audit', action],
    queryFn: ({ pageParam }) =>
      apiPage<AuditLogDto>('/admin/audit', { action, limit: 30, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div>
      <PageHeader title={t('audit.title')} subtitle={t('audit.subtitle')} />
      <div className="mb-4 max-w-sm">
        <Field label={t('audit.action')}>
          <TextInput value={action} onChange={(event) => setAction(event.target.value)} />
        </Field>
      </div>
      {query.isLoading ? <Spinner /> : null}
      {query.isError ? <ErrorBlock message={t('errorTitle')} onRetry={() => void query.refetch()} /> : null}
      {!query.isLoading && items.length === 0 ? <Empty title={t('audit.empty')} /> : null}
      <ul className="divide-y divide-border rounded-2xl border border-border bg-card">
        {items.map((event) => (
          <li key={event.id}>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm"
              onClick={() => setOpenId(openId === event.id ? null : event.id)}
            >
              <span className="font-medium">{event.action}</span>
              <span className="text-fg-muted">{event.actorName || event.actorType}</span>
              <span className="text-fg-muted">{format.when(event.createdAt)}</span>
            </button>
            {openId === event.id ? (
              <div className="grid gap-3 px-4 pb-4 md:grid-cols-2">
                <pre className="overflow-auto rounded-xl bg-muted p-3 text-xs">
                  {JSON.stringify(event.before ?? null, null, 2)}
                </pre>
                <pre className="overflow-auto rounded-xl bg-muted p-3 text-xs">
                  {JSON.stringify(event.after ?? null, null, 2)}
                </pre>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {query.hasNextPage ? (
        <Button className="mt-4" variant="secondary" onClick={() => void query.fetchNextPage()}>
          {t('loadMore')}
        </Button>
      ) : null}
    </div>
  );
}
